/*
 * Раздел «Feast»: состояние feature store кластера из метрик (VictoriaMetrics через proxy
 * /prometheus/<кластер>/api) — материализация, online store по feature view, сервинг.
 * У Feast нет REST-API реестра, доступного снаружи кластера (реестр — gRPC, UI — protobuf за
 * oauth2-proxy), поэтому источник — метрики платформы: CronJob материализации, custom query
 * CNPG по таблицам online store, метрики feature server.
 */
import { Entity } from '@backstage/catalog-model';
import {
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
  StatusError,
  StatusOK,
  StatusPending,
  StatusWarning,
  Table,
  TableColumn,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Box, Grid, Tooltip, Typography } from '@material-ui/core';
import useAsync from 'react-use/esm/useAsync';
import { DATASTORE_CLUSTER_ANNOTATION, annotation } from '../../lib/viva';
import {
  feastLastMaterializeQuery,
  feastMaterializeFailedQuery,
  feastNotFoundRatioQuery,
  feastOnlineRowsQuery,
  feastOnlineRpsQuery,
  feastOnlineWritesQuery,
  feastServersQuery,
  queryInstant,
} from '../../lib/prometheus';
import { AsyncCell } from '../catalog/cells';

export type FeastState = {
  lastMaterialize?: number; // unix seconds
  materializeFailed: number;
  featureViews: { table: string; rows: number; writesPerDay: number }[];
  onlineRps: number;
  notFoundRatio: number;
  servers: Record<string, number>;
};

const hoursAgo = (unix?: number) =>
  unix ? Math.max(0, (Date.now() / 1000 - unix) / 3600) : undefined;

const fmtAgo = (unix?: number): string => {
  const h = hoursAgo(unix);
  if (h === undefined) return 'ещё не было';
  if (h < 1) return `${Math.round(h * 60)} мин назад`;
  if (h < 48) return `${Math.round(h)} ч назад`;
  return `${Math.round(h / 24)} дн назад`;
};

/** Одним заходом: все запросы состояния Feast кластера. */
export const useFeastState = (entity: Entity) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const cluster = annotation(entity, DATASTORE_CLUSTER_ANNOTATION);
  return useAsync(async (): Promise<FeastState> => {
    if (!cluster)
      throw new Error(`нет аннотации ${DATASTORE_CLUSTER_ANNOTATION}`);
    const apis = { discoveryApi, fetchApi };
    const q = (query: string) => queryInstant(apis, cluster, query);
    const [last, failed, rows, writes, rps, notFound, servers] =
      await Promise.all([
        q(feastLastMaterializeQuery()),
        q(feastMaterializeFailedQuery()),
        q(feastOnlineRowsQuery()),
        q(feastOnlineWritesQuery()),
        q(feastOnlineRpsQuery()),
        q(feastNotFoundRatioQuery()),
        q(feastServersQuery()),
      ]);
    const writesBy = Object.fromEntries(
      writes.map(s => [s.metric.relname, s.value]),
    );
    return {
      lastMaterialize: last[0]?.value,
      materializeFailed: failed[0]?.value ?? 0,
      featureViews: rows
        .map(s => ({
          table: s.metric.relname,
          rows: s.value,
          writesPerDay: writesBy[s.metric.relname] ?? 0,
        }))
        .sort((a, b) => a.table.localeCompare(b.table)),
      onlineRps: rps[0]?.value ?? 0,
      notFoundRatio: Number.isFinite(notFound[0]?.value)
        ? notFound[0].value
        : 0,
      servers: Object.fromEntries(
        servers.map(s => [s.metric.deployment, s.value]),
      ),
    };
  }, [cluster, discoveryApi, fetchApi]);
};

const MaterializeStatus = ({ state }: { state: FeastState }) => {
  const h = hoursAgo(state.lastMaterialize);
  const text = fmtAgo(state.lastMaterialize);
  if (state.materializeFailed > 0)
    return <StatusError>ошибка, последняя удачная {text}</StatusError>;
  if (h === undefined) return <StatusPending>{text}</StatusPending>;
  if (h > 48) return <StatusWarning>{text}</StatusWarning>;
  return <StatusOK>{text}</StatusOK>;
};

/** Ячейка таблицы раздела: материализация и число feature view в online store. */
export const EntityFeastStateCell = ({ entity }: { entity: Entity }) => {
  const state = useFeastState(entity);
  return (
    <AsyncCell state={state} unavailable="метрики недоступны">
      {s => (
        <Tooltip
          title={`feature view в online store: ${
            s.featureViews.map(f => f.table).join(', ') || 'нет'
          }; ${s.onlineRps.toFixed(2)} rps`}
        >
          <Typography variant="body2" noWrap component="div">
            <MaterializeStatus state={s} /> · {s.featureViews.length} FV
          </Typography>
        </Tooltip>
      )}
    </AsyncCell>
  );
};

const fvColumns: TableColumn<FeastState['featureViews'][number]>[] = [
  {
    title: 'Таблица online store (<проект>_<feature view>)',
    field: 'table',
    highlight: true,
  },
  { title: 'Строк', render: r => r.rows.toLocaleString('ru-RU') },
  {
    title: 'Записей за сутки',
    render: r => Math.round(r.writesPerDay).toLocaleString('ru-RU'),
  },
];

/** Карточки страницы сущности feast-<кластер>. */
export const EntityFeastCards = () => {
  const { entity } = useEntity();
  const state = useFeastState(entity);
  const uiLink = entity.metadata.links?.find(l => l.title === 'Feast UI')?.url;
  if (state.loading) {
    return (
      <Grid item md={12} xs={12}>
        <InfoCard title="Feature store">
          <Progress />
        </InfoCard>
      </Grid>
    );
  }
  if (state.error) {
    return (
      <Grid item md={12} xs={12}>
        <InfoCard title="Feature store">
          <ResponseErrorPanel error={state.error} />
        </InfoCard>
      </Grid>
    );
  }
  const s = state.value!;
  const servers = [
    'feast-registry',
    'feast-online',
    'feast-offline',
    'feast-ui',
  ];
  return (
    <>
      <Grid item md={6} xs={12}>
        <InfoCard
          title="Материализация и серверы"
          action={uiLink ? <Link to={uiLink}>Feast UI</Link> : undefined}
        >
          <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
            <Typography variant="body2" component="div">
              Материализация offline → online: <MaterializeStatus state={s} />
              {s.materializeFailed > 0
                ? ` (неудачных Job за сутки: ${s.materializeFailed})`
                : ''}
            </Typography>
            <Typography variant="body2" component="div">
              Серверы:{' '}
              {servers.map(d => (
                <span key={d} style={{ marginRight: 12 }}>
                  {(s.servers[d] ?? 0) > 0 ? (
                    <StatusOK>{d.replace('feast-', '')}</StatusOK>
                  ) : (
                    <StatusError>{d.replace('feast-', '')}</StatusError>
                  )}
                </span>
              ))}
            </Typography>
            <Typography variant="body2" component="div">
              Сервинг признаков: {s.onlineRps.toFixed(2)} запросов/с ·{' '}
              {s.notFoundRatio > 0.5 ? (
                <StatusWarning>
                  {Math.round(s.notFoundRatio * 100)}% признаков не найдено
                </StatusWarning>
              ) : (
                <StatusOK>
                  {Math.round(s.notFoundRatio * 100)}% не найдено
                </StatusOK>
              )}
            </Typography>
          </Box>
        </InfoCard>
      </Grid>
      <Grid item md={6} xs={12}>
        <InfoCard
          title="Online store по feature view"
          subheader="Материализованные признаки в Postgres (таблицы <проект>_<feature view>)"
        >
          {s.featureViews.length === 0 ? (
            <Box p={1}>
              <Typography variant="body2" color="textSecondary">
                Материализованных feature view нет — примените определения
                (feast:apply в репозитории признаков) и дождитесь
                материализации.
              </Typography>
            </Box>
          ) : (
            <Table
              options={{
                paging: false,
                search: false,
                toolbar: false,
                padding: 'dense',
              }}
              data={s.featureViews}
              columns={fvColumns}
            />
          )}
        </InfoCard>
      </Grid>
    </>
  );
};
